/**
 * F12-P1 — Pantalla para quien tiene la solicitud APROBADA pero la app no le
 * ve ninguna membresía, así que no puede entrar a su club.
 *
 * Antes, estas cuentas caían al wizard de registro una y otra vez: llenaban el
 * formulario, se creaba otra solicitud, el director la volvía a aprobar, y
 * vuelta a empezar (29 solicitudes duplicadas en producción salieron de ahí).
 *
 * La primera versión de esta pantalla daba UNA sola explicación —"tu ficha
 * quedó vinculada a un correo anterior"— y le pedía a la persona que gestionara
 * el traslado de su ficha. Al medir en producción el 2026-08-06 resultó que de
 * las 55 personas atascadas, CERO estaban en ese caso: 20 tenían la solicitud
 * apuntando a un club que se renombró o se fusionó, 27 tenían todo bien salvo
 * el documento de membresía, y 8 no tenían ficha. O sea que la pantalla le
 * decía algo falso a todo el que la veía, y lo mandaba a pedir un traslado
 * que no hacía falta (o que le habría movido una ficha que estaba bien).
 *
 * Ahora averigua cuál de los tres casos es y dice qué pedir en cada uno.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { Button } from "@/components/ui";
import {
  CheckCircle2,
  KeyRound,
  LogOut,
  Mail,
  RefreshCw,
  Building2,
  UserPlus,
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { joinRequestService } from "@/lib/services/joinRequests";
import { personService } from "@/lib/services/persons";
import { db } from "@/lib/firebase";

type Caso =
  | "cargando"
  /** Su ficha está vinculada a OTRA cuenta. */
  | "ficha_en_otra_cuenta"
  /** La solicitud apunta a un club que se renombró, se fusionó o ya no está. */
  | "club_cambiado"
  /** Ficha y club correctos: sólo falta que lo agreguen al club. */
  | "falta_membresia";

export default function ApprovedUnlinkedPage() {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuthStore();
  const [caso, setCaso] = useState<Caso>("cargando");
  const [clubPedido, setClubPedido] = useState("");
  const [clubHoy, setClubHoy] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!profile?.id) return;
      try {
        const req = await joinRequestService.getUserApprovedRequest(profile.id);
        if (cancelled || !req) return;
        const pedido = req.clubName ?? "";
        setClubPedido(pedido);

        // 1) ¿La ficha es de otra cuenta? Es lo único que obliga a mover datos.
        if (req.idDocumentNumber) {
          const res = await personService.checkDocumentStatus(
            req.idDocumentType ?? "CC",
            req.idDocumentNumber,
            profile.id,
          );
          if (cancelled) return;
          if (res.status === "linked_other" || res.status === "guardian") {
            if (res.maskedEmail) setMaskedEmail(res.maskedEmail);
            setCaso("ficha_en_otra_cuenta");
            return;
          }
        }

        // 2) ¿El club de la solicitud sigue siendo el mismo? Tras una fusión o
        //    un cambio de nombre, la solicitud queda apuntando a otra cosa.
        if (req.clubId) {
          const snap = await getDoc(doc(db, "clubs", req.clubId));
          if (cancelled) return;
          const actual = snap.exists()
            ? (snap.data() as { name?: string; status?: string })
            : null;
          setClubHoy(actual?.name ?? "");
          const cambiado =
            !actual ||
            actual.status !== "active" ||
            (!!pedido && actual.name !== pedido);
          if (cambiado) {
            setCaso("club_cambiado");
            return;
          }
        }

        // 3) Todo cuadra: lo que falta es que lo agreguen como miembro.
        setCaso("falta_membresia");
      } catch (e) {
        console.warn("[ApprovedUnlinked] no se pudo resolver el detalle:", e);
        if (!cancelled) setCaso("falta_membresia");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile?.id]);

  // "Ya lo arreglaron": recargar para que initialize() reevalúe el estado.
  const recheck = () => {
    setChecking(true);
    window.location.reload();
  };

  const titulo =
    caso === "club_cambiado"
      ? "Tu club cambió de nombre"
      : caso === "falta_membresia"
        ? "Falta un último paso en tu club"
        : "Tu solicitud ya fue aprobada";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{titulo}</h1>
            {clubPedido && (
              <p className="text-sm text-gray-500">Club {clubPedido}</p>
            )}
          </div>
        </div>

        {caso === "cargando" && (
          <p className="text-gray-500 mt-4">Revisando tu caso…</p>
        )}

        {caso === "ficha_en_otra_cuenta" && (
          <>
            <p className="text-gray-700 mt-4">
              No necesitas volver a llenar el formulario. Lo que falta es{" "}
              <b>conectar tu ficha con esta cuenta</b>: tus datos quedaron
              registrados con un correo anterior
              {maskedEmail ? (
                <>
                  {" "}
                  (<span className="font-mono text-gray-900">{maskedEmail}</span>
                  )
                </>
              ) : null}
              , por eso al entrar aquí no ves tu club.
            </p>
            <div className="mt-5 space-y-3">
              <div className="flex gap-3 p-3 rounded-xl bg-primary-50 border border-primary-100">
                <KeyRound className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-semibold text-gray-900">
                    Si ese correo es tuyo
                  </p>
                  <p>
                    Cierra sesión y entra con él. Si no recuerdas la contraseña,
                    usa «¿Olvidaste tu contraseña?» en la pantalla de inicio.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
                <Mail className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-semibold text-gray-900">
                    Si ya no tienes acceso a ese correo, o era de un familiar
                  </p>
                  <p>
                    Escríbele al director de tu club (o a soporte) y pide el{" "}
                    <b>traslado de tu ficha</b> a{" "}
                    <span className="font-mono">{user?.email}</span>. Es un
                    cambio de un minuto y conservas todo tu historial.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {caso === "club_cambiado" && (
          <>
            <p className="text-gray-700 mt-4">
              Tus datos están bien y tu solicitud fue aprobada. Lo que pasa es
              que <b>el club al que entraste ya no se llama así</b>
              {clubHoy ? (
                <>
                  : hoy figura como{" "}
                  <span className="font-semibold text-gray-900">{clubHoy}</span>
                </>
              ) : (
                " (se fusionó con otro o dejó de estar activo)"
              )}
              . Por eso la plataforma no logra conectarte con él.
            </p>
            <div className="mt-5 flex gap-3 p-3 rounded-xl bg-primary-50 border border-primary-100">
              <Building2 className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-semibold text-gray-900">Qué pedir</p>
                <p>
                  Escríbele a tu director o a soporte y cuéntales esto tal cual:{" "}
                  <b>
                    «mi solicitud quedó en {clubPedido || "mi club anterior"}
                    {clubHoy ? `, que ahora es ${clubHoy}` : ""}»
                  </b>
                  . Lo reconectan en un minuto y no pierdes nada de tu
                  historial. <b>No vuelvas a llenar el formulario</b>: se crean
                  solicitudes repetidas y no arregla el problema.
                </p>
              </div>
            </div>
          </>
        )}

        {caso === "falta_membresia" && (
          <>
            <p className="text-gray-700 mt-4">
              Tus datos están completos y tu solicitud fue aprobada. Sólo falta
              que <b>te agreguen a la lista de miembros</b> del club — es un
              paso que hace el director desde su pantalla, y sin él la
              plataforma no te muestra nada.
            </p>
            <div className="mt-5 flex gap-3 p-3 rounded-xl bg-primary-50 border border-primary-100">
              <UserPlus className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-semibold text-gray-900">Qué pedir</p>
                <p>
                  Escríbele al director de{" "}
                  <b>{clubPedido || "tu club"}</b> y pídele que te{" "}
                  <b>agregue como miembro</b>; tu solicitud ya está aprobada.{" "}
                  <b>No vuelvas a llenar el formulario</b>: se crean solicitudes
                  repetidas y no arregla el problema.
                </p>
              </div>
            </div>
          </>
        )}

        <div className="flex flex-col sm:flex-row gap-2 mt-6">
          <Button onClick={recheck} disabled={checking} className="flex-1">
            <RefreshCw
              className={`w-4 h-4 mr-2 ${checking ? "animate-spin" : ""}`}
            />
            Ya lo arreglaron, revisar
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              await signOut();
              navigate("/login");
            }}
            className="flex-1"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>

        <p className="text-xs text-gray-400 mt-5 text-center">
          Sesión: {user?.email}
        </p>
      </div>
    </div>
  );
}
